import {
  Alert,
  Button,
  Header,
  SpaceBetween,
  Table,
} from '@cloudscape-design/components';
import InventoryMetric from 'features/inventory/components/InventoryMetric';
import activateProductVersion from 'features/products/apis/activateProductVersion';
import ProductStatusBadge from 'features/products/components/ProductStatusBadge';
import useProductVersions from 'features/products/hooks/useProductVersions';
import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';

import CreateProductVersionModal from './CreateProductVersionModal';

export default function ProductVersionsSection({
  product,
  onRefreshProduct,
  onRefreshActiveVersion,
}) {
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [editVersion, setEditVersion] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [activatingVersionId, setActivatingVersionId] = useState('');
  const {
    data: versions = [],
    isLoading,
    refetch,
  } = useProductVersions(product.id);

  const columnDefinitions = useMemo(() => [
    {
      id: 'version',
      header: 'Version',
      cell: (item) => item.id,
    },
    {
      id: 'status',
      header: 'Status',
      // eslint-disable-next-line react/no-unstable-nested-components
      cell: (item) => <ProductStatusBadge status={item.status} />,
    },
    {
      id: 'mapping',
      header: 'Internal Mapping',
      cell: (item) => (
        [item.projectId, item.pPartId].filter(Boolean).join('-') || '-'
      ),
    },
    {
      id: 'onHand',
      header: 'On Hand',
      // eslint-disable-next-line react/no-unstable-nested-components
      cell: (item) => <InventoryMetric productDocId={product.id} versionId={item.id} link />,
    },
    {
      id: 'reorderPolicy',
      header: 'Reorder',
      cell: (item) => (item.reorderPolicy?.enabled ? 'Enabled' : 'Off'),
    },
    {
      id: 'triggerQuantity',
      header: 'Trigger',
      cell: (item) => item.reorderPolicy?.triggerQuantity ?? '-',
    },
    {
      id: 'targetLevel',
      header: 'Target',
      cell: (item) => item.reorderPolicy?.targetLevel ?? '-',
    },
    {
      id: 'unitPrice',
      header: 'Unit Price',
      cell: (item) => `$${item.unitPrice.toFixed(2)}`,
    },
    {
      id: 'leadtimeDays',
      header: 'Lead Time',
      cell: (item) => (item.leadtimeDays ? `${item.leadtimeDays} days` : '-'),
    },
    {
      id: 'createdAt',
      header: 'Created',
      cell: (item) => item.createdAt?.toDate?.().toLocaleString() || '-',
    },
    {
      id: 'actions',
      header: 'Actions',
      minWidth: 150,
      // eslint-disable-next-line react/no-unstable-nested-components
      cell: (item) => (
        <SpaceBetween direction="horizontal" size="xs">
          <Button variant="normal" onClick={() => setEditVersion(item)}>
            Edit
          </Button>
          {item.id !== product.activeVersionId && (
            <Button
              variant="normal"
              loading={activatingVersionId === item.id}
              onClick={async () => {
                try {
                  setErrorMessage('');
                  setActivatingVersionId(item.id);
                  await activateProductVersion({
                    productDocId: product.id,
                    versionId: item.id,
                  });
                  await Promise.all([refetch(), onRefreshProduct(), onRefreshActiveVersion()]);
                } catch (error) {
                  setErrorMessage(error.message || 'Unable to activate version');
                } finally {
                  setActivatingVersionId('');
                }
              }}
            >
              Activate
            </Button>
          )}
        </SpaceBetween>
      ),
    },
  ], [
    activatingVersionId,
    onRefreshActiveVersion,
    onRefreshProduct,
    product.activeVersionId,
    product.id,
    refetch,
  ]);

  return (
    <>
      <Table
        header={(
          <Header
            variant="h2"
            actions={(
              <Button variant="primary" onClick={() => setOpenCreateModal(true)}>
                Create version
              </Button>
            )}
          >
            Product Versions
          </Header>
        )}
        items={versions}
        loading={isLoading}
        columnDefinitions={columnDefinitions}
        empty={<div>No versions found</div>}
      />
      {errorMessage && (
        <Alert type="error">{errorMessage}</Alert>
      )}
      {openCreateModal && (
        <CreateProductVersionModal
          open={openCreateModal}
          product={product}
          onClose={() => setOpenCreateModal(false)}
          onSuccess={async () => {
            await Promise.all([refetch(), onRefreshProduct(), onRefreshActiveVersion()]);
          }}
        />
      )}
      {editVersion && (
        <CreateProductVersionModal
          open={Boolean(editVersion)}
          product={product}
          isEditing
          initVersion={editVersion}
          onClose={() => setEditVersion(null)}
          onSuccess={async () => {
            setEditVersion(null);
            await Promise.all([refetch(), onRefreshProduct(), onRefreshActiveVersion()]);
          }}
        />
      )}
    </>
  );
}

ProductVersionsSection.propTypes = {
  product: PropTypes.shape({
    id: PropTypes.string.isRequired,
    sku: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    activeVersionId: PropTypes.string,
  }).isRequired,
  onRefreshProduct: PropTypes.func.isRequired,
  onRefreshActiveVersion: PropTypes.func.isRequired,
};
